import { Building2, Award, Lightbulb, Handshake } from "lucide-react";
import SEO from "@/components/SEO";

export default function About() {
  const principles = [
    {
      icon: Building2,
      title: "Operational Excellence",
      description: "We identify companies with strong fundamentals and proven track records, then enhance their operations through best-in-class governance and strategic planning."
    },
    {
      icon: Award,
      title: "Quality Over Quantity",
      description: "Our selective approach focuses on acquiring exceptional companies rather than pursuing volume. Each acquisition undergoes rigorous due diligence to ensure strategic fit."
    },
    {
      icon: Lightbulb,
      title: "Innovation & Technology",
      description: "We invest in modern logistics technology and data-driven decision making to keep our portfolio companies competitive in an evolving industry."
    },
    {
      icon: Handshake,
      title: "Partnership Mindset",
      description: "We work alongside existing management teams, providing resources, expertise, and strategic guidance while respecting operational autonomy."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Us - Stanford Logistics Management"
        description="Stanford Logistics Management is a specialized acquisition platform focused on the trucking and logistics industry, combining deep sector expertise with disciplined capital deployment."
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground py-16 lg:py-24">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-6">
              About Stanford Logistics Management
            </h1>
            <p className="text-xl text-primary-foreground/90 leading-relaxed">
              A sponsor-driven platform dedicated to building value in the trucking and logistics sector through strategic acquisitions and operational excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6 text-foreground">
                Who We Are
              </h2>
              <div className="space-y-4 text-foreground/70 leading-relaxed">
                <p>
                  Stanford Logistics Management is a specialized acquisition platform focused exclusively on the trucking and logistics industry. We combine deep sector expertise with disciplined capital deployment to identify and acquire high-quality companies with strong operational foundations.
                </p>
                <p>
                  Our team brings decades of combined experience in logistics operations, private equity, and strategic management. This expertise allows us to quickly assess opportunities, execute transactions efficiently, and implement value-creation initiatives that drive sustainable growth.
                </p>
                <p>
                  We understand that the logistics industry is undergoing significant transformation. From driver shortages and regulatory changes to technological disruption and evolving customer expectations, today's operators face complex challenges. Our platform provides the strategic oversight, operational resources, and capital support needed to navigate these dynamics successfully.
                </p>
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl overflow-hidden">
              <img 
                src="/trucks-fleet.jpg" 
                alt="Professional trucking fleet" 
                className="w-full h-64 object-cover"
                loading="lazy"
              />
              <div className="p-8 lg:p-12">
              <h3 className="text-2xl font-bold mb-6 text-foreground">Our Focus</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                  <span className="text-foreground/70">Truckload and less-than-truckload (LTL) carriers</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                  <span className="text-foreground/70">Third-party logistics (3PL) providers</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                  <span className="text-foreground/70">Specialized freight and dedicated contract carriers</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                  <span className="text-foreground/70">Companies with strong management teams and operational track records</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                  <span className="text-foreground/70">Businesses positioned for growth through improved operations and strategic direction</span>
                </li>
              </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

       {/* Leadership Team */}
      
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Leadership Team
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Stanford Logistics Management is led by a team of seasoned professionals with decades of combined experience in logistics, operations, finance, and business development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Buddy Lichter */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-foreground">Buddy Lichter</h3>
                <p className="text-sm text-red-accent font-semibold">Chairman</p>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p><span className="font-semibold text-foreground">Proven Operator:</span> Owner and CEO of Go Green Logistics with 20+ years leading national logistics operations across truckload, LTL and refrigerated freight.</p>
                <p><span className="font-semibold text-foreground">Strategic Perspective:</span> Deep expertise in fleet optimization and technology integration to support scaling initiatives.</p>
                <p><span className="font-semibold text-foreground">Value Creation:</span> Operational insight and leadership strengthen ability to generate sustainable growth and capture new market opportunities across the freight sector.</p>
              </div>
            </div>

            {/* Tim Lambright */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-foreground">Tim Lambright</h3>
                <p className="text-sm text-red-accent font-semibold">CEO</p>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p><span className="font-semibold text-foreground">Leadership:</span> Over 20 years of experience leading teams and organizations across technology development, renewable energy, and behavioral health sectors.</p>
                <p><span className="font-semibold text-foreground">Strategic Planning:</span> Demonstrated record of delivering successful outcomes by consistently aligning financial strategy with operational priorities and long-term company goals.</p>
                <p><span className="font-semibold text-foreground">Analytical Acumen:</span> Experience leveraging operational and financial data to inform strategic decision-making and allocate resources to enhance growth and performance.</p>
              </div>
            </div>

            {/* Barry Waxler */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-foreground">Barry Waxler</h3>
                <p className="text-sm text-red-accent font-semibold">Director of Logistics Management</p>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p><span className="font-semibold text-foreground">Industry Network:</span> Over three decades of experience in manufacturing, import/export and trucking industries, bringing substantial network of connections and relationships essential for strategic partnerships.</p>
                <p><span className="font-semibold text-foreground">Strategic Insights:</span> Deep understanding of warehousing and logistics, coupled with strategic insights, makes him invaluable asset contributing to operational excellence, efficient supply chain management, and driving unprecedented growth.</p>
              </div>
            </div>

            {/* Joshua Gross */}
<div className="bg-card rounded-xl p-6 border border-border">
  <div className="mb-4">
    <h3 className="text-xl font-bold text-foreground">Joshua Gross</h3>
    <p className="text-sm text-red-accent font-semibold">CRO</p>
  </div>
  <div className="space-y-3 text-sm text-muted-foreground">
    <p>
      <span className="font-semibold text-foreground">Intermodal Expertise:</span>
      In-depth knowledge of Gulf and East Coast operations, providing valuable asset for expanded operations.
    </p>
    <p>
      <span className="font-semibold text-foreground">M&amp;A Experience:</span>
      Background as Investment Banker at Bear Stearns specializing in M&amp;A within Healthcare and Technology sectors equips him to identify and capitalize on growth opportunities.
    </p>
    <p>
      <span className="font-semibold text-foreground">Project Leadership:</span>
      Successful transformation of healthcare facility into student housing complex demonstrates ability to lead complex projects from conception to execution.
    </p>
    <p>
      <span className="font-semibold text-foreground">Revenue Growth:</span>
      As Chief Revenue Officer at Intermodal company, significantly increased revenue and expanded services nationwide, showcasing ability to foster partnerships and drive business growth.
    </p>
  </div>
</div>


            {/* Yisroel Cherns */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-foreground">Yisroel Cherns</h3>
                <p className="text-sm text-red-accent font-semibold">Advisory Board Member</p>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p><span className="font-semibold text-foreground">Entrepreneurial Leadership:</span> Extensive experience and proven track record of successful business management, with ability to assemble and lead teams of top-tier professionals.</p>
                <p><span className="font-semibold text-foreground">Asset Management:</span> Owns and manages approximately 1.9 million square feet of industrial space, providing deep industry insight and logistical expertise in handling multifaceted operations.</p>
                <p><span className="font-semibold text-foreground">Healthcare Expertise:</span> Successful track record in healthcare investments provides acumen and connections to navigate complex regulatory concerns and secure lucrative contracts in transportation of medical supplies or personnel.</p>
              </div>
            </div>

            {/* Harlan Lyons */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-foreground">Harlan Lyons</h3>
                <p className="text-sm text-red-accent font-semibold">Advisory Board Member</p>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p><span className="font-semibold text-foreground">Visionary Leadership:</span> Building and instilling visionary leadership into startups and growth-stage companies for more than 25 years, with experience as CEO and/or President of more than 11 companies.</p>
                <p><span className="font-semibold text-foreground">Fortune 500 Relationships:</span> Negotiated key customer agreements with Fortune 500 companies including IBM, Microsoft, Radio Shack, Avnet, Conoco-Phillips, and Toshiba.</p>
                <p><span className="font-semibold text-foreground">Telecommunications & Technology Expertise:</span> Worked for companies such as US Cellular and Cellular One, implementing revenue-building and growth strategies.</p>
                <p><span className="font-semibold text-foreground">Capital Formation:</span> Raised hundreds of millions of dollars for start-up launch, turnaround, and business growth. Founded ASK Consulting Group in 1998, providing strategic guidance to emerging companies.</p>
              </div>
            </div>
          </div>
        </div>
      

      {/* Our Principles */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-foreground">
              Our Principles
            </h2>
            <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
              Guiding values that shape our investment decisions and partnership approach
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <div
                  key={index}
                  className="bg-card rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-border"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-accent" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-3 text-card-foreground">
                        {principle.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {principle.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-8 text-center text-foreground">
              Our Investment Approach
            </h2>
            <div className="space-y-8">
              <div className="bg-card rounded-xl p-8 border border-border">
                <h3 className="text-xl font-semibold mb-4 text-card-foreground">Identification</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We leverage our industry network and market intelligence to identify high-quality trucking and logistics companies with strong fundamentals, experienced management teams, and clear paths to value creation. Our focus is on businesses that have demonstrated operational excellence and possess competitive advantages in their markets.
                </p>
              </div>

              <div className="bg-card rounded-xl p-8 border border-border">
                <h3 className="text-xl font-semibold mb-4 text-card-foreground">Acquisition</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Our acquisition process is thorough yet efficient. We conduct comprehensive due diligence covering operations, financials, regulatory compliance, and market positioning. We structure transactions that align incentives and ensure smooth transitions, often retaining existing management teams who know the business best.
                </p>
              </div>

              <div className="bg-card rounded-xl p-8 border border-border">
                <h3 className="text-xl font-semibold mb-4 text-card-foreground">Growth</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Post-acquisition, we implement strategic initiatives focused on operational improvements, technology adoption, market expansion, and talent development. We provide governance oversight while empowering management teams to execute on growth plans. Our goal is to build sustainable value through a combination of organic growth and strategic add-on acquisitions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
